import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';
import { IProductDetailDialogContentProps } from './content';
import { ProductImageBrowser } from '../../../app-shop/shop/components/pdct-image-browser';
import { RatingBar } from '../../../app-shop/components/rating-bar';
import { BwjButton, BwjButtonType } from '../../../framework/components/bwj-button';
import { InfoLine } from '../../../app-shop/shop/components/info-line';
import { MeaurementRequestNote } from './measure-req-note';
import { ProductVariantSelector } from '../../../app-shop/shop/components/pdct-variant-select';
import { isMeaningfulString } from '../../../framework/utils/string';
import { RatingBreakdown } from '../../../app-shop/shop/components/rating-breakdown';
import { MeasurementRequestForm } from '../measurement-request-form';

const getStyleClasses = (theme: ITheme) => {
  const normalSpace = '0.75rem';
  const bigSpace = '1rem';
  const tinySpace = '0.1rem';
  const megaSpace = '2.5rem';

  const root: IStyle = {
    margin: 0,
    padding: 0,
  };

  const topFold: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: megaSpace,
  };

  const mediaContainer: IStyle = {}

  const detailsContainer: IStyle = {
    width: 1,
    flexGrow: 1,
    paddingLeft: bigSpace,
  }

  const brand: IStyle = {
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    fontWeight: 400,
    margin: 0,
    padding: 0,
  };

  const title: IStyle = {
    fontSize: theme.fonts.large.fontSize,
    lineHeight: theme.fonts.large.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginBottom: normalSpace,
  };

  const keyStatsContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: normalSpace,
    marginBottom: normalSpace,
  };

  const priceContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    alignItems: 'flex-start',
  };

  const variantSelector: IStyle = {
    marginBottom: bigSpace,
  };

  const subheading: IStyle = {
    fontSize: theme.fonts.mediumPlus.fontSize,
    lineHeight: theme.fonts.mediumPlus.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginTop: bigSpace,
    marginBottom: normalSpace,
  };

  const actionContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: '2.5rem',
    gap: bigSpace,
  }

  const buttonContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: tinySpace,
  };

  const section: IStyle = {
    margin: 0,
    padding: 0,

    '& > ul': {
      margin: 0,
    },

    '& p': {
      margin: 0,
      padding: 0,
      flexGrow: 1,
      maxWidth: '55%',
    },
  }

  const reviewContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: megaSpace,

    '& > div': {
      flexGrow: 1,
    },
  };

  return mergeStyleSets({
    root,
    topFold,
    mediaContainer,
    detailsContainer,
    brand,
    title,
    keyStatsContainer,
    priceContainer,
    variantSelector,
    subheading,
    actionContainer,
    buttonContainer,
    section,
    reviewContainer,
  });
}

export const ProductDetailDialogContentDesktop: React.FunctionComponent<IProductDetailDialogContentProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { contentWidth } = props;
  const mediaWidth = useMemo(() => {
    let mw = Math.ceil(contentWidth / 2);
    if(mw < 320) {
      mw = 320;
    } else if ((contentWidth - mw) > 500) {
      mw = contentWidth - 500;
    }
    return mw;
  }, [contentWidth]);


  return (
    <div className={cs.root}>
      <div className={cs.topFold}>
        <div className={cs.mediaContainer}>
          <ProductImageBrowser
            id={props.id}
            idPrefix='prd-dtl'
            images={props.imageUrls}
            imageWidth={mediaWidth}
          />
        </div>
        {props.showMeasurementRequestForm === false ?
        <div className={cs.detailsContainer}>
          <h3 className={cs.brand}>{props.brand}</h3>
          <h2 className={cs.title}>{props.title}</h2>

          <div className={cs.keyStatsContainer}>
            {!!props.rating && !!props.averageRating &&
            <RatingBar
              rating={props.averageRating}
              reviewerCount={props.rating.totalReviewCount}
              rootStyle={{ width: '75%', maxWidth: 250 }}
            />}
            {props.measurementRequestDate === null ?
            <div className={cs.priceContainer}>
              <BwjButton
                buttonStyle={BwjButtonType.PrimaryStandard}
                text={'Request pricing'}
                onClick={() => props.onToggleMeasurementRequestForm(true)}
              />
              <InfoLine
                icon={'LightBulb'}
                text={'Why can\'t you just tell me the price?'}
                onClick={props.onShowPricingExplanation}
              />
            </div>
            :
            <MeaurementRequestNote requestDate={props.measurementRequestDate} />}
          </div>

          <div className={cs.variantSelector}>
            <h3 className={cs.variantSelector}>
              {`Available ${props.variantDescriptor}:`}
            </h3>
            <ProductVariantSelector
              swatchSize={5}
              options={props.variants}
              selected={props.selectedVariant}
              showLabels={true}
              onSelectionChanged={s => !!s && props.onVariantSelected(s)}
            />
          </div>
        </div>
        :
        <div className={cs.detailsContainer}>
          <MeasurementRequestForm
            formModel={props.requestMeasurementFormModel}
            onRequestSubmitted={props.onMeasurementRequestSubmitted}
            onDismiss={() => props.onToggleMeasurementRequestForm(false)}
          />
        </div>}
      </div>
      
      {props.showMeasurementRequestForm === false &&
      <>
      <section className={cs.section}>
        <h3 className={cs.subheading}>Key Features</h3>
        <ul>
          {props.keyFeatures.map(feature =>
          <li key={feature}>{feature}</li>)}
        </ul>
      </section>
      {!!props.rating && !!props.averageRating &&
      <section className={cs.section}>
        <h3 className={cs.subheading}>{`Average Customer Rating: ${props.averageRating.toFixed(1)}`}</h3>
        <div className={cs.reviewContainer}>
          {isMeaningfulString(props.rating.reviewSummary) &&
          <p>{props.rating.reviewSummary}</p>}
          <RatingBreakdown review={props.rating} />
        </div>
      </section>}
      </>}
    </div>
  );
};

